// src/pages/Farming.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Leaf, 
  Shield, 
  TrendingUp, 
  Users, 
  Award,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  CheckCircle,
  Sprout
} from 'lucide-react';
// Header is now in PublicLayout

// Import farming images
import livestock1 from '../assets/images/slideshow/livestock-1.jpg';
import livestock2 from '../assets/images/slideshow/livestock-2.jpg';

const Farming = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const values = [
    {
      icon: Shield,
      title: 'Ethical Practices',
      description: 'Committed to humane and sustainable livestock management.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Environmental responsibility in every aspect of our operations.'
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'Rigorous standards ensuring the highest quality livestock.'
    },
    {
      icon: Users,
      title: 'Community Impact',
      description: 'Supporting local farmers and strengthening rural economies.'
    }
  ];

  const services = [
    {
      title: 'Livestock Trading',
      description: 'Premium quality livestock sourced from trusted farms across South Africa.',
      features: ['Cattle', 'Sheep', 'Goats', 'Poultry']
    },
    {
      title: 'Agricultural Consulting',
      description: 'Expert guidance for sustainable farming practices and business growth.',
      features: ['Farm Management', 'Best Practices', 'Market Access', 'Financial Planning']
    },
    {
      title: 'Supply Chain Solutions',
      description: 'Efficient distribution networks connecting producers with markets.',
      features: ['Logistics', 'Quality Control', 'Traceability', 'Market Integration']
    }
  ];

  const contactInfo = {
    phone: '+27 (0) 67 820 1034',
    email: 'info@pontsoyarona.co.za',
    address: 'Johannesburg, Gauteng'
  };

  return (
    <div className="relative min-h-screen bg-pontso-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-20">
        {/* Background with farming imagery */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${livestock1})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-pontso-black via-pontso-black/90 to-pontso-black" />
          <div className="absolute inset-0 bg-gradient-to-t from-pontso-farming/20 via-transparent to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Icon */}
            <motion.div
              className="mb-8"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="w-20 h-20 mx-auto bg-pontso-gold/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-pontso-gold/30">
                <Sprout className="w-10 h-10 text-pontso-gold" />
              </div>
            </motion.div>

            <h1 className={`font-heading text-white mb-6 tracking-tight ${isMobile ? 'text-5xl' : 'text-7xl lg:text-8xl'}`}>
              Livestock Trading
            </h1>
            
            <div className="w-24 h-px bg-pontso-gold mx-auto mb-6" />
            
            <p className={`text-white/90 font-body font-light mb-12 leading-relaxed ${isMobile ? 'text-lg px-4' : 'text-2xl lg:text-3xl max-w-4xl mx-auto'}`}>
              Ethical, sustainable, and quality-driven agricultural solutions
            </p>

            {/* CTA Buttons */}
            <motion.div 
              className={`flex flex-col sm:flex-row gap-4 justify-center ${isMobile ? 'px-4' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-pontso-gold text-pontso-black rounded-lg hover:bg-pontso-gold-light transition-all duration-300 font-medium group"
              >
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-pontso-gold text-pontso-gold rounded-lg hover:bg-pontso-gold/10 transition-all duration-300 font-body font-light"
              >
                Our Services
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="relative z-10 py-32 bg-pontso-black-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-pontso-charcoal">
                <img
                  src={livestock2}
                  alt="Livestock farming"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="text-sm font-medium text-pontso-gold uppercase tracking-wider">
                Our Mission
              </span>
              
              <h2 className="text-4xl lg:text-5xl font-heading font-normal text-white leading-tight">
                Building sustainable agricultural
                <span className="block text-pontso-gold">empires</span>
              </h2>
              
              <div className="w-16 h-px bg-pontso-gold" />
              
              <div className="space-y-4 text-lg text-white/80 font-body font-light leading-relaxed">
                <p>
                  At Pontso Yarona Group, our livestock trading division represents our commitment 
                  to ethical agriculture and sustainable practices. We connect quality producers 
                  with discerning markets, ensuring every transaction upholds the highest standards 
                  of animal welfare and environmental responsibility.
                </p>
                
                <p>
                  Our approach goes beyond trading – we build lasting partnerships with farmers, 
                  provide expert consulting, and create value chains that benefit entire communities. 
                  We believe in agriculture that sustains not just businesses, but the land and 
                  people who depend on it.
                </p>
              </div>

              <div className="pt-4">
                <a 
                  href="#contact"
                  className="inline-flex items-center space-x-2 text-pontso-gold font-medium hover:text-pontso-gold-light transition-colors"
                >
                  <span>Partner with us</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative z-10 py-32 bg-pontso-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-pontso-gold uppercase tracking-wider">
              Our Values
            </span>
            <h2 className="text-4xl lg:text-5xl font-heading font-normal text-white mt-6 mb-6">
              What drives us
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto" />
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center space-y-4"
                >
                  <div className="w-20 h-20 mx-auto bg-pontso-charcoal rounded-2xl flex items-center justify-center border border-pontso-gold/20 hover:border-pontso-gold transition-colors">
                    <IconComponent className="w-10 h-10 text-pontso-gold" />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-heading font-normal text-white mb-2">
                      {value.title}
                    </h3>
                    <p className="text-white/70 font-body font-light leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative z-10 py-32 bg-pontso-black-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-pontso-gold uppercase tracking-wider">
              Our Services
            </span>
            <h2 className="text-4xl lg:text-5xl font-heading font-normal text-white mt-6 mb-6">
              Comprehensive agricultural solutions
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto" />
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-pontso-charcoal border border-pontso-gold/20 rounded-2xl p-8 hover:border-pontso-gold transition-all duration-300"
              >
                <h3 className="text-2xl font-heading font-normal text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-white/70 font-body font-light mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-white/60 text-sm font-body font-light">
                      <CheckCircle className="w-4 h-4 text-pontso-gold mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative z-10 py-32 bg-pontso-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div>
              <h2 className="text-4xl lg:text-5xl font-heading font-normal text-white mb-6">
                Let's Work Together
              </h2>
              <div className="w-24 h-px bg-pontso-gold mx-auto" />
            </div>

            <p className="text-xl text-white/70 font-body font-light max-w-2xl mx-auto">
              Whether you're a farmer looking to expand, a buyer seeking quality livestock, 
              or a partner interested in sustainable agriculture, we'd love to hear from you.
            </p>

            <div className="grid sm:grid-cols-3 gap-8">
              {[
                { icon: Phone, label: contactInfo.phone, href: `tel:${contactInfo.phone}` },
                { icon: Mail, label: contactInfo.email, href: `mailto:${contactInfo.email}` },
                { icon: MapPin, label: contactInfo.address, href: '#' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group p-8 bg-pontso-charcoal border border-pontso-gold/20 rounded-2xl hover:border-pontso-gold transition-all duration-300"
                  >
                    <div className="w-16 h-16 mx-auto mb-6 bg-pontso-charcoal rounded-2xl flex items-center justify-center group-hover:bg-pontso-gold/10 border border-pontso-gold/20 group-hover:border-pontso-gold transition-colors">
                      <IconComponent className="w-8 h-8 text-pontso-gold" />
                    </div>
                    <div className="text-base text-white/90 font-body font-light">
                      {item.label}
                    </div>
                  </motion.a>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="pt-8"
            >
              <a
                href="mailto:info@pontsoyarona.co.za"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-pontso-gold text-pontso-black rounded-lg hover:bg-pontso-gold-light transition-all duration-300 font-medium text-lg shadow-xl"
              >
                <Mail className="w-5 h-5" />
                <span>Get in Touch</span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-pontso-charcoal px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-white font-heading text-xl">
              Pontso Yarona Group
            </div>
            <div className="text-sm text-white/60 font-body font-light text-center sm:text-right">
              © 2024 Pontso Yarona Group. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Farming;

